import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, LogOut, Menu, X, ChevronDown, ChevronRight, Settings, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { cn } from '@/lib/utils';

interface AdminLayoutProps { children: ReactNode; }

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  {
    icon: FileText, label: 'Blog', path: '/admin/blog',
    children: [
      { label: 'All Posts', path: '/admin/blog' },
      { label: 'New Post', path: '/admin/blog/new' },
      { label: 'Bulk Upload', path: '/admin/blog/bulk' },
      { label: 'Auto Generate', path: '/admin/blog/auto' },
      { label: 'Queue & Scheduler', path: '/admin/blog/queue' },
      { label: 'Auto-Blog Settings', path: '/admin/blog/auto-settings' },
      { label: 'Authors', path: '/admin/blog/authors' },
      { label: 'Categories', path: '/admin/blog/categories' },
      { label: 'IndexNow', path: '/admin/blog/indexnow' },
      { label: 'Clicks', path: '/admin/blog/clicks' },
    ]
  },
  { icon: Users, label: 'Team Access', path: '/admin/users' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];


const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { user, signOut, isAdmin } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['/admin/blog']);

  const handleSignOut = async () => { await signOut(); navigate('/admin/login'); };
  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const isActive = (path: string) => path === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-muted/30 flex">
      <aside className={cn("bg-background border-r border-border transition-all duration-300 flex flex-col", sidebarOpen ? "w-64" : "w-16")}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {sidebarOpen && <span className="font-bold text-lg text-primary">ACG CMS</span>}
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => (
            <div key={item.path}>
              {item.children ? (
                <>
                  <button onClick={() => toggleMenu(item.path)} className={cn("w-full flex items-center gap-3 px-4 py-3 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors", isActive(item.path) && "bg-primary/10 text-primary border-r-2 border-primary")}>
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && (<><span className="flex-1 text-left">{item.label}</span>{expandedMenus.includes(item.path) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</>)}
                  </button>
                  {sidebarOpen && expandedMenus.includes(item.path) && (
                    <div className="bg-muted/50">
                      {item.children.map((child) => (
                        <Link key={child.path} to={child.path} className={cn("flex items-center gap-3 pl-12 pr-4 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors", location.pathname === child.path && "text-primary font-medium")}>{child.label}</Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link to={item.path} className={cn("flex items-center gap-3 px-4 py-3 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors", isActive(item.path) && "bg-primary/10 text-primary border-r-2 border-primary")}>
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground">{isAdmin ? 'Admin' : 'Editor'}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleSignOut}><LogOut className="h-4 w-4" /></Button>
            </div>
          ) : (
            <Button variant="ghost" size="icon" onClick={handleSignOut}><LogOut className="h-4 w-4" /></Button>
          )}
        </div>
      </aside>
      <main className="flex-1 overflow-auto"><div className="p-6">{children}</div></main>
    </div>
  );
};

export default AdminLayout;
